import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { Search } from '../svgs';
import { InputField, InputFieldProps, InputFieldRef } from './InputField';
import { Popover } from 'radix-ui';
import { Button } from '../Button';
import { twMerge } from 'tailwind-merge';
import { X } from 'lucide-react';

export type SearchInputFieldOption = {
  id: string;
  label: string;
  value: string;
};

export interface SearchInputFieldProps extends Omit<
  InputFieldProps,
  'endAdornment' | 'endActionComponent'
> {
  options?: SearchInputFieldOption[];
  onSearch?: (searchText: string) => void;
  onExpand?: () => void;
  onCollapse?: () => void;

  clearLabel?: string;
  // onShrink?: () => void;
}

export const SearchInputField = ({
  onChange: propOnChange,
  options,
  value,
  onSearch,
  onExpand,
  onCollapse,
  containerClassName,
  fieldsetClassName,
  clearLabel = 'Clear',
  ...otherProps
}: SearchInputFieldProps) => {
  const inputFieldRef = useRef<InputFieldRef>(null);

  const [_value, setValue] = useState<string>();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFocusing, setFocusing] = useState<boolean>(false);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue(e.currentTarget.value);
      propOnChange?.(e);
    },
    [propOnChange]
  );

  const open = (options?.length ?? 0) > 0;
  const isEmpty = (_value?.trim().length ?? 0) === 0;

  return (
    <Popover.Root open={open}>
      <Popover.Anchor className={fieldsetClassName}>
        <InputField
          ref={inputFieldRef}
          fieldsetClassName={fieldsetClassName}
          containerClassName={twMerge('flex-1', containerClassName)}
          value={value ?? _value}
          endAdornment={
            <button
              type="button"
              onClick={() => {
                setIsExpanded(prev => {
                  const next = !prev;
                  inputFieldRef.current?.setCompact(!next);
                  if (next) {
                    onExpand?.();
                    requestAnimationFrame(() => inputFieldRef.current?.focusInput());
                  } else {
                    setValue('');
                    onCollapse?.();
                  }
                  return next;
                });

                if (_value) {
                  onSearch?.(_value);
                }
              }}
            >
              <Search height={20} width={20} />
            </button>
          }
          onChange={onChange}
          onFocus={() => {
            setFocusing(true);
          }}
          onBlur={() => {
            setFocusing(false);
          }}
          endActionComponent={
            isExpanded ? (
              <Button
                variant="outlined"
                color="blue"
                onClick={() => {
                  setValue('');
                  setIsExpanded(false);
                  inputFieldRef.current?.setCompact(true);
                  onCollapse?.();
                  propOnChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
                }}
                aria-label={clearLabel}
              >
                <X size={16} />
              </Button>
            ) : null
          }
          {...otherProps}
        />
      </Popover.Anchor>
      <Popover.Content>
        {options?.map(option => {
          return <div></div>;
        })}
      </Popover.Content>
    </Popover.Root>
  );
};
